import { colorStrings } from "@bevco/Utilities/variables";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Tab, { TabProps } from "@insite/mobius/Tab";
import TabGroup from "@insite/mobius/TabGroup";
import { resolveColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import debounce from "lodash/debounce";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css, FlattenSimpleInterpolation, ThemeProps, withTheme } from "styled-components";

const enum fields {
    showTabs = "showTabs",
    displayShowTabs = "Show as tabs (when off, only Instant Registration Form is visible)",
}

export interface OwnProps extends WidgetProps {
    fields: {
        [fields.showTabs]: boolean;
    };
}

export const mapStateToProps = (_: ApplicationState, __: OwnProps) => {
    return {};
};

export type Props = OwnProps & ThemeProps<BaseTheme> & ReturnType<typeof mapStateToProps>;

export interface TabWidgetStyles {
    selectStyles: SelectPresentationProps;
}

export const tabWidgetStyles: TabWidgetStyles = {
    selectStyles: {
        cssOverrides: {
            inputSelect: css`
                background: none !important;
                border: none;
                border-bottom: 1px solid ${getColor(colorStrings.primaryMain)};
                &:focus {
                    border: none;
                    border-bottom: 1px solid ${getColor(colorStrings.primaryMain)};
                    outline: none;
                }
            `,
        },
        iconProps: {
            css: css`
                transition: transform 0.5s ease;
                &.rotated {
                    transform: rotate(180deg);
                }
            `,
        },
    },
};

export const styles = tabWidgetStyles;

const tabGroup = css`
    padding-right: 0;
    padding-left: 0;
`;

const tabContent = css`
    border-bottom: none;
    padding: 32px 0;
`;

const tabCss: FlattenSimpleInterpolation = css`
    color: ${({ selected, theme }: TabProps) =>
        selected ? resolveColor("common.global.gray01", theme!) : resolveColor("primary.main", theme!)};
    border-bottom: 2px solid
        ${({ selected, theme }: TabProps) => (selected ? resolveColor("primary.main", theme!) : "transparent")};
    transition: all 0.6s;
    &:hover {
        border-bottom: 2px solid
            ${({ selected, theme }: TabProps) => (selected ? resolveColor("primary.main", theme!) : "transparent")};
        ${({ selected }: TabProps) =>
            !selected
                ? `
                cursor: pointer;
                span {
                color: #a2160c;
                text-decoration: underline;
                text-underline-offset: 2px;}`
                : ""};
    }

    &:focus {
        outline-color: transparent;
        outline-style: none;
        outline-width: 0;
    }
    width: 50%;
` as FlattenSimpleInterpolation;

export const TabWidget = ({ id, fields, theme: { breakpoints } }: Props) => {
    const showTabs = fields?.showTabs ?? true;
    const currentTab = "current_customer";
    const tab1headline = translate("I Am A Current BEVCO Customer (Account # Required)");
    const tab2headline = translate("I Am New Customer");
    const [tabSelected, setTabSelected] = useState(tab1headline);
    const [isMobile, setIsMobile] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        const handleWindowSize = () => {
            const maxMobileIdx = breakpoints.keys.indexOf("md");
            const maxMobile = breakpoints.values[maxMobileIdx];
            setIsMobile(window.innerWidth < maxMobile);
        };
        handleWindowSize();
        const debouncedHandleWindowSize = debounce(handleWindowSize, 200);
        window.addEventListener("resize", debouncedHandleWindowSize);
        // Cleanup event listeners on unmount
        return () => {
            window.removeEventListener("resize", debouncedHandleWindowSize);
        };
    }, []);

    const tabChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setTabSelected(event.target.value);
    };

    const changeTab = (event: React.MouseEvent) => {
        const target = event.target as HTMLElement;
        setTabSelected(target.textContent || "");
    };

    const handleClick = () => {
        const dropdown = document.getElementById("select_tabwidget");
        const dropdownIcon = dropdown ? dropdown.nextElementSibling : null;
        if (dropdownIcon && dropdownIcon.tagName === "SPAN") {
            dropdownIcon.classList.toggle("rotated", isOpen);
        }
        setIsOpen(prev => !prev);
    };

    if (!showTabs) {
        return (
            <StyledWrapper>
                <Zone contentId={id as never} zoneName={"FirstTab" as never} />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper>
            {isMobile ? (
                <>
                    <Select
                        value={tabSelected || ""}
                        onChange={tabChangeHandler}
                        onClick={handleClick}
                        onFocus={() => setIsOpen(true)}
                        onBlur={() => setIsOpen(false)}
                        {...tabWidgetStyles.selectStyles}
                        id="select_tabwidget"
                    >
                        <option key={tab1headline} value={tab1headline}>
                            {tab1headline}
                        </option>
                        <option key={tab2headline} value={tab2headline}>
                            {tab2headline}
                        </option>
                    </Select>
                    {tabSelected === tab1headline && <Zone contentId={id as never} zoneName={"FirstTab" as never} />}
                    {tabSelected === tab2headline && <Zone contentId={id as never} zoneName={"SecondTab" as never} />}
                </>
            ) : (
                <TabGroup
                    id={id as never}
                    onClick={changeTab}
                    current={currentTab}
                    cssOverrides={{ tabGroup, tabContent }}
                >
                    <Tab
                        data-test-selector="current_customer"
                        headline={tab1headline}
                        key="current_customer"
                        tabKey="current_customer"
                        css={tabCss}
                    >
                        <Zone contentId={id as never} zoneName={"FirstTab" as never} />
                    </Tab>
                    <Tab
                        data-test-selector="new_customer"
                        headline={tab2headline}
                        key="new_customer"
                        tabKey="new_customer"
                        css={tabCss}
                    >
                        <Zone contentId={id as never} zoneName={"SecondTab" as never} />
                    </Tab>
                </TabGroup>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withTheme(TabWidget)) as any,
    definition: {
        displayName: "Tab",
        group: "Create Account",
        icon: "arrows-maximize",
        fieldDefinitions: [
            {
                name: fields.showTabs,
                displayName: fields.displayShowTabs,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
